import { CircleAlert, Clock, FileText, CircleHelp, Wrench, EyeOff } from "lucide-react";

const t = {
  fr: {
    title: "Le Problème",
    subtitle: "Un Écosystème Fragmenté Qui Demande une Modernisation",
    cards: [
      { icon: Clock, title: "Plateformes Désuètes", desc: "Standards de design 2010–2016, mauvaise expérience utilisateur. Les outils actuels sont lents, visuellement datés et frustrants pour tous les utilisateurs." },
      { icon: FileText, title: "Processus Manuels", desc: "Formulaires papier et réconciliation manuelle causant des erreurs. Des heures perdues chaque semaine en tâches administratives répétitives." },
      { icon: CircleHelp, title: "Confusion des Parents", desc: "Recommencer à zéro chaque fois que l'école change de traiteur, que le traiteur modifie ses processus ou protocoles, ou encore lors d'un déménagement ou d'un changement d'école." },
      { icon: Wrench, title: "Inefficacité des Traiteurs", desc: "Jongler avec plusieurs outils et processus déconnectés. Aucune vue unifiée de la production, des commandes et de la facturation." },
      { icon: EyeOff, title: "Invisibilité des Écoles", desc: "Manque de visibilité et de contrôle sur les opérations quotidiennes. Les écoles n'ont aucun moyen de superviser la qualité en temps réel." },
    ],
    resultsTitle: "Cela résulte en :",
    results: ["Erreurs", "Surcharge administrative", "Risques de paiement", "Frustration des parents", "Inefficacité des fournisseurs"],
    conclusion: "Le marché est mûr pour la modernisation.",
  },
  en: {
    title: "The Problem",
    subtitle: "A Fragmented Ecosystem That Demands Modernization",
    cards: [
      { icon: Clock, title: "Outdated Platforms", desc: "2010–2016 design standards, poor user experience. Current tools are slow, visually dated and frustrating for all users." },
      { icon: FileText, title: "Manual Processes", desc: "Paper forms and manual reconciliation causing errors. Hours wasted each week on repetitive administrative tasks." },
      { icon: CircleHelp, title: "Parent Confusion", desc: "Starting over each time the school changes caterer, the caterer modifies processes, or when moving to a new school." },
      { icon: Wrench, title: "Caterer Inefficiency", desc: "Juggling multiple disconnected tools and processes. No unified view of production, orders and billing." },
      { icon: EyeOff, title: "School Invisibility", desc: "Lack of visibility and control over daily operations. Schools have no way to supervise quality in real time." },
    ],
    resultsTitle: "This results in:",
    results: ["Errors", "Administrative overload", "Payment risks", "Parent frustration", "Supplier inefficiency"],
    conclusion: "The market is ripe for modernization.",
  },
};

export default function ProblemSection({ lang }) {
  const c = t[lang];
  return (
    <section id="problem" data-testid="problem-section" className="py-16 sm:py-24 bg-darker">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 sm:mb-20">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-destructive/20 mb-6 icon-neon-red">
            <CircleAlert className="h-8 w-8 sm:h-10 sm:w-10 text-destructive" strokeWidth={1.5} />
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">{c.title}</h2>
          <p className="text-lg sm:text-xl text-white/70 px-4">{c.subtitle}</p>
        </div>
        <div className="max-w-5xl mx-auto space-y-8 sm:space-y-12 mb-16 sm:mb-20">
          {c.cards.map((card, idx) => {
            const Icon = card.icon;
            const isEven = idx % 2 === 0;
            return (
              <div key={card.title} data-testid={`problem-card-${idx}`} className="group">
                <div className={`flex flex-col ${isEven ? "md:flex-row" : "md:flex-row-reverse"} items-stretch gap-0 rounded-2xl border border-destructive/20 bg-dark overflow-hidden hover:border-destructive/50 transition-all duration-500 hover:shadow-[0_0_40px_rgba(239,68,68,0.08)]`}>
                  <div className="flex-shrink-0 w-full md:w-48 lg:w-56 bg-gradient-to-br from-destructive/10 via-destructive/5 to-transparent flex items-center justify-center py-10 md:py-0 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-[radial-gradient(circle_at_center,rgba(239,68,68,0.12)_0%,transparent_70%)]" />
                    <div className="icon-neon-red relative z-10 transition-transform duration-500 group-hover:scale-110">
                      <Icon className="h-12 w-12 sm:h-16 sm:w-16 text-destructive" strokeWidth={1.2} />
                    </div>
                  </div>
                  <div className="flex-1 p-6 sm:p-8 lg:p-10 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-black tracking-widest text-destructive/50 uppercase">0{idx + 1}</span>
                      <div className="h-px flex-1 bg-gradient-to-r from-destructive/30 to-transparent" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 group-hover:text-destructive transition-colors duration-300">{card.title}</h3>
                    <p className="text-sm sm:text-base text-white/60 leading-relaxed">{card.desc}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border bg-gradient-to-b from-destructive/10 to-dark border-destructive/30 overflow-hidden">
            <div className="p-6 sm:p-8 lg:p-10">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-6 text-center">{c.resultsTitle}</h3>
              <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-6">
                {c.results.map((r) => (
                  <div key={r} className="inline-flex items-center rounded-full border border-destructive/40 bg-destructive/10 text-destructive text-xs sm:text-sm px-4 py-1.5 font-semibold icon-neon-red">{r}</div>
                ))}
              </div>
              <div className="h-px bg-gradient-to-r from-transparent via-destructive/30 to-transparent mb-6" />
              <p className="text-lg sm:text-xl font-bold text-center text-primary" style={{ textShadow: "0 0 20px rgba(182,236,19,0.5)" }}>{c.conclusion}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
